<template>
	<div>
		<b-alert :show="!!error" variant="danger">{{error?.error||error}}</b-alert>
		<div class="d-flex flex-wrap mb-3" style="gap: 0.5rem">
			<b-button variant="secondary" class="flex-fill" to="/widgets/voices?lang=en"><b-icon icon="soundwave" class="mr-1" />TTS Voices</b-button>
			<b-button variant="secondary" class="flex-fill" to="/widgets/replacements"><b-icon icon="file-earmark-font-fill" class="mr-1" />Word replacements</b-button>
			<b-button variant="secondary" class="flex-fill" to="/widgets/event-media"><b-icon icon="music-note-beamed" class="mr-1" />Alert sounds</b-button>
			<b-button variant="secondary" class="flex-fill" to="/widgets/dono-media"><b-icon icon="piggy-bank-fill" class="mr-1" />Platform alert sounds</b-button>
			<b-button variant="secondary" class="flex-fill" to="/widgets/tower-detector"><b-icon icon="sort-alpha-down" class="mr-1" />Tower detector</b-button>
			<b-button variant="secondary" class="flex-fill" to="/widgets/thresholds"><b-icon icon="bar-chart-fill" class="mr-1" />Song request thresholds</b-button>
		</div>
		<div class="widget-grid">
			<div class="widget-grid-item">
				<widget-card
					name="Chat widget"
					:widget-url="`${$baseUrl}/widget/chat.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					:widget-popout-height="600"
					settings-path="/widgets/chat"
					refresh-type="chat"
					:popout-url="`${$baseUrl}/app/chatWidgetSettings.html`"
					:popout-height="300"
					:loading="chatLoading"
				>
					<template #preview><chat-widget-preview :settings="widgetSettings['chat']" :speed=".8" :initial-messages="2" /></template>
					<template #quick-settings><chat-widget-settings-content @loading="chatLoading = $event" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Alert widget"
					:widget-url="`${$baseUrl}/widget/gift.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/gift"
					:widget-popout-width="600"
					:widget-popout-height="400"
					refresh-type="gift"
					:popout-url="`${$baseUrl}/app/alertWidgetSettings.html`"
					:popout-height="320"
					:loading="alertLoading"
				>
					<template #preview><gift-widget-preview :settings="widgetSettings['gift']" /></template>
					<template #quick-settings><alert-widget-settings-content @loading="alertLoading = $event" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Viewer count widget"
					:widget-url="`${$baseUrl}/widget/viewerCount.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/viewer-count"
					refresh-type="viewer-count"
					:widget-popout-width="widgetSettings['viewer-count'].orientation == 'horizontal' ? 400 : 100"
					:widget-popout-height="widgetSettings['viewer-count'].orientation == 'horizontal' ? 100 : 400"
				>
					<template #preview><viewer-count-widget-preview :settings="widgetSettings['viewer-count']" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Follower goal"
					:widget-url="`${$baseUrl}/widget/followerGoal.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					:widget-popout-width="700"
					:widget-popout-height="100"
					settings-path="/widgets/follower-goal"
					refresh-type="follower-goal"
					:popout-url="`${$baseUrl}/app/followerGoalSettings.html`"
					:popout-height="280"
					:loading="followerGoalLoading"
				>
					<template #preview><goal-widget-preview :settings="widgetSettings['follower-goal']" /></template>
					<template #quick-settings><follower-goal-settings-content @loading="followerGoalLoading = $event" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<b-overlay :show="!!(user.id && !user.is_subscribed)" variant="dark" opacity="0.75">
					<widget-card
						name="Dono goal"
						:widget-url="`${$baseUrl}/widget/giftGoal.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
						:widget-popout-width="700"
						:widget-popout-height="100"
						settings-path="/widgets/gift-goal"
						refresh-type="gift-goal"
						:popout-url="`${$baseUrl}/app/giftGoalSettings.html`"
						:popout-height="280"
						:loading="giftGoalLoading"
					>
						<template #preview><gift-goal-widget-preview :settings="widgetSettings['gift-goal']" /></template>
						<template #quick-settings><gift-goal-settings-content @loading="giftGoalLoading = $event" /></template>
					</widget-card>
					<template #overlay>
						<div class="text-center mx-n3">
							<p class="mb-3">The Dono Goal widget is available for subscribed users only.</p>
							<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
						</div>
					</template>
				</b-overlay>
			</div>
			<div class="widget-grid-item">
				<b-overlay :show="!!(user.id && !user.is_subscribed)" variant="dark" opacity="0.75">
					<widget-card
						name="Membership goal"
						:widget-url="`${$baseUrl}/widget/subGoal.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
						:widget-popout-width="700"
						:widget-popout-height="100"
						settings-path="/widgets/sub-goal"
						refresh-type="sub-goal"
						:popout-url="`${$baseUrl}/app/subGoalSettings.html`"
						:popout-height="280"
						:loading="subGoalLoading"
					>
						<template #preview><goal-widget-preview :settings="widgetSettings['sub-goal']" /></template>
						<template #quick-settings><sub-goal-settings-content @loading="subGoalLoading = $event" /></template>
					</widget-card>
					<template #overlay>
						<div class="text-center mx-n3">
							<p class="mb-3">The membership goal widget is available for subscribed users only.</p>
							<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
						</div>
					</template>
				</b-overlay>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Highlighted message"
					:widget-url="`${$baseUrl}/widget/highlightedMessage.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/highlighted-message"
					refresh-type="highlighted-message"
					:widget-popout-width="600"
					:widget-popout-height="400"
				>
					<template #preview><highlighted-message-widget-preview :settings="widgetSettings['highlighted-message']" /></template>
					<template #extra-actions>
						<b-dropdown-divider />
						<b-dropdown-item @click="clearHighlightedMessage"><b-icon icon="x-circle" class="mr-1" />Clear current message</b-dropdown-item>
					</template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Scroller widget"
					:widget-url="`${$baseUrl}/widget/scroller.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/scroller"
					refresh-type="scroller"
				>
					<template #preview><scroller-widget-preview :settings="widgetSettings['scroller']" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Top donation"
					:widget-url="`${$baseUrl}/widget/topDonation.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/top-donation"
					refresh-type="top-donation"
				>
					<template #preview><latest-event-widget-preview :settings="widgetSettings['top-donation']" sample-name="ExampleUser" :sample-amount="10" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Latest follower"
					:widget-url="`${$baseUrl}/widget/latestFollower.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/latest-follower"
					refresh-type="latest-follower"
				>
					<template #preview><latest-event-widget-preview :settings="widgetSettings['latest-follower']" sample-name="ExampleUser" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Latest member"
					:widget-url="`${$baseUrl}/widget/latestSubscriber.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/latest-subscriber"
					refresh-type="latest-subscriber"
				>
					<template #preview><latest-event-widget-preview :settings="widgetSettings['latest-subscriber']" sample-name="ExampleUser" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<widget-card
					name="Latest donation"
					:widget-url="`${$baseUrl}/widget/latestDonation.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
					settings-path="/widgets/latest-donation"
					refresh-type="latest-donation"
				>
					<template #preview><latest-event-widget-preview :settings="widgetSettings['latest-donation']" sample-name="ExampleUser" :sample-amount="10" /></template>
				</widget-card>
			</div>
			<div class="widget-grid-item">
				<b-overlay :show="!!(user.id && !user.is_subscribed)" variant="dark" opacity="0.75">
					<widget-card
						name="Emote wall"
						:widget-url="`${$baseUrl}/widget/emoteWall.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
						settings-path="/widgets/emote-wall"
						refresh-type="emote-wall"
					>
						<template #preview><emote-wall-widget-preview :settings="widgetSettings['emote-wall']" :real-emotes="realEmotes" :real-gift-types="realGiftTypes" /></template>
					</widget-card>
					<template #overlay>
						<div class="text-center mx-n3">
							<p class="mb-3">The emote wall widget is available for subscribed users only.</p>
							<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
						</div>
					</template>
				</b-overlay>
			</div>
			<div class="widget-grid-item">
				<b-overlay :show="!!(user.id && !user.is_subscribed)" variant="dark" opacity="0.75">
					<widget-card
						name="Word cloud"
						:widget-url="`${$baseUrl}/widget/wordCloud.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
						:widget-popout-width="600"
						:widget-popout-height="400"
						settings-path="/widgets/word-cloud"
						refresh-type="word-cloud"
					>
						<template #preview><word-cloud-widget-preview :settings="widgetSettings['word-cloud']" /></template>
					</widget-card>
					<template #overlay>
						<div class="text-center mx-n3">
							<p class="mb-3">The word cloud widget is available for subscribed users only.</p>
							<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
						</div>
					</template>
				</b-overlay>
			</div>
			<div class="widget-grid-item">
				<b-overlay :show="!!(user.id && !user.is_subscribed)" variant="dark" opacity="0.75">
					<widget-card
						name="Ban reaper"
						:widget-url="`${$baseUrl}/widget/banReaper.html?hash=${encodeURIComponent(user.widget_hash||'')}`"
						:widget-popout-width="600"
						:widget-popout-height="400"
						settings-path="/widgets/ban-reaper"
						refresh-type="ban-reaper"
						:virtual-width="1920"
						:virtual-height="1080"
					>
						<template #preview><ban-reaper-widget-preview :settings="widgetSettings['ban-reaper']" /></template>
					</widget-card>
					<template #overlay>
						<div class="text-center mx-n3">
							<p class="mb-3">The ban reaper widget is available for subscribed users only.</p>
							<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
						</div>
					</template>
				</b-overlay>
			</div>
		</div>
	</div>
</template>

<script>
import WidgetCard from '@/components/Widget/WidgetCard.vue';
import ChatWidgetSettingsContent from '@/components/Widget/ChatWidgetSettingsContent.vue';
import AlertWidgetSettingsContent from '@/components/Widget/AlertWidgetSettingsContent.vue';
import GiftGoalSettingsContent from '@/components/Widget/GiftGoalSettingsContent.vue';
import SubGoalSettingsContent from '@/components/Widget/SubGoalSettingsContent.vue';
import FollowerGoalSettingsContent from '@/components/Widget/FollowerGoalSettingsContent.vue';
import ChatWidgetPreview from '@/components/Widget/ChatWidgetPreview.vue';
import GoalWidgetPreview from '@/components/Widget/GoalWidgetPreview.vue';
import ViewerCountWidgetPreview from '@/components/Widget/ViewerCountWidgetPreview.vue';
import HighlightedMessageWidgetPreview from '@/components/Widget/HighlightedMessageWidgetPreview.vue';
import LatestEventWidgetPreview from '@/components/Widget/LatestEventWidgetPreview.vue';
import ScrollerWidgetPreview from '@/components/Widget/ScrollerWidgetPreview.vue';
import GiftWidgetPreview from '@/components/Widget/GiftWidgetPreview.vue';
import GiftGoalWidgetPreview from '@/components/Widget/GiftGoalWidgetPreview.vue';
import EmoteWallWidgetPreview from '@/components/Widget/EmoteWallWidgetPreview.vue';
import WordCloudWidgetPreview from '@/components/Widget/WordCloudWidgetPreview.vue';
import BanReaperWidgetPreview from '@/components/Widget/BanReaperWidgetPreview.vue';

export default {
	components: {
		WidgetCard,
		ChatWidgetSettingsContent,
		AlertWidgetSettingsContent,
		GiftGoalSettingsContent,
		SubGoalSettingsContent,
		FollowerGoalSettingsContent,
		ChatWidgetPreview,
		GoalWidgetPreview,
		ViewerCountWidgetPreview,
		HighlightedMessageWidgetPreview,
		LatestEventWidgetPreview,
		ScrollerWidgetPreview,
		GiftWidgetPreview,
		GiftGoalWidgetPreview,
		EmoteWallWidgetPreview,
		WordCloudWidgetPreview,
		BanReaperWidgetPreview,
	},
	data() { return {
		error: null,
		user: {},
		chatLoading: false,
		alertLoading: false,
		giftGoalLoading: false,
		subGoalLoading: false,
		followerGoalLoading: false,
		widgetSettings: {
			'chat': {},
			'follower-goal': {},
			'viewer-count': {},
			'highlighted-message': {},
			'latest-follower': {},
			'latest-subscriber': {},
			'scroller': {},
			'gift': {},
			'gift-goal': {},
			'sub-goal': {},
			'emote-wall': {},
			'word-cloud': {},
			'latest-donation': {},
			'top-donation': {},
			'ban-reaper': {},
		},
		realEmotes: [],
		realGiftTypes: [],
	}},
	async mounted() {
		try {
			this.error = null;
			this.user = await this.$api.get('userData');
			this.$api.get('emotes/mine').then(emotes => { this.realEmotes = emotes || []; }, () => {});
			this.$api.get('userGiftTypes', { limit: 1000 }).then(r => { this.realGiftTypes = r?.results || []; }, () => {});
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				userChanged: async () => { this.user = await this.$api.get('userData'); },
				widgetSettingsFieldChange: msg => this.widgetSettings[msg.widgetType][msg.field] = msg.value,
			});
		} catch(e) {
			console.error(e);
			this.error = e;
		}
		Promise.all(Object.keys(this.widgetSettings).map(async slug => {
			try {
				this.widgetSettings[slug] = await this.$api.get(`widgetSettings/${slug}`) || {};
			} catch(e) {
				console.error(`Failed to load ${slug} widget settings`, e);
			}
		}));
	},
	beforeDestroy() {
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		clearHighlightedMessage() {
			this.$api.post('selectMessage', null).catch(e => console.error('Clear highlighted message failed', e));
		},
	},
};
</script>

<style scoped>
.widget-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 1rem;
	align-items: start;
}

@media (min-width: 768px) {
	.widget-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (min-width: 992px) {
	.widget-grid {
		grid-template-columns: repeat(3, 1fr);
	}
}
</style>
